import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';

import { errorMessage } from '@/shared/api/errors';
import { apiUrl } from '@/shared/config';
import { failureText, isRetrying, retryLine } from '@/shared/emails/failureCodes';
import { rescueFailureText } from '@/shared/emails/mailFailure';
import { actionInFlight, rescueInFlight } from '@/shared/emails/useEmails';
import { useDuplicateQuote } from '@/shared/quotes/useQuotes';
import { LoadAsTariffDialog, type TariffAttachment } from '@/shared/tariffs/LoadAsTariff';
import { isTariffFile } from '@/shared/tariffs/types';
import { CATEGORY_LABELS, DESTINATION_LABELS } from '@/shared/triage/vocabulary';
import { AiNotice } from '@/shared/ui/AiNotice';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Spinner } from '@/shared/ui/Spinner';
import { FailureCode, TechnicalDetails } from '@/shared/ui/TechnicalDetails';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CopyIcon,
  ForwardIcon,
  InboxIcon,
  MailIcon,
  PaperclipIcon,
  RefreshIcon,
  ReplyAllIcon,
  ReplyIcon,
  TrashIcon,
  UploadIcon,
} from '@/shared/ui/icons';
import { useIsPlatform } from '@/shared/session/currentUser';
import { useCan } from '@/shared/session/profiles';
import { formatBytes, formatDate } from '@/shared/utils/format';

import { emailKeys, emailsApi, type EmailListParams } from './api';

import { ComposeDialog, SentFromHere } from './Compose';
import { Conversation } from './Conversation';
import { EmailBody } from './EmailBody';
import { OutOfInboxNotice } from './OutOfInbox';
import { attachmentContentId, inlineImagesFor, referencedContentIds } from './emailHtml';
import { AnsweredBadge } from './Answered';
import { SpamBadge } from './SpamBadge';
import { StatusBadge } from './StatusBadge';
import { TriagePanel } from './TriagePanel';
import {
  decisionCategory,
  isUnread,
  liveDecision,
  MANUAL_MAILBOX,
  sortedDecisions,
  type EmailDetail,
  mailboxLabel,
  type OutgoingKind,
} from './types';
import {
  useDownloadAttachment,
  useEmailDetail,
  useMarkRead,
  useMarkUnread,
  useMoveEmail,
  useNeighbours,
  useReprocess,
  useRescue,
} from './useEmails';
import { emailHref, folderShortName, inboxHref, listFilters, PARAM } from './views';

/** «11 sept 2026, 16:31»: the line under the subject. */
const headerDateFormat = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const HELP = [
  <>
    Mira lo que el sistema dice que es y a quién va. Si está bien, pulsa <strong>Correcto</strong>;
    si no, <strong>Corregir…</strong> y elige qué es y a quién va. Después,{' '}
    <strong>Siguiente por revisar</strong> abre el siguiente.
  </>,
  <>
    <strong>Correcto también ordena tu correo</strong>: el mensaje sale de la bandeja de entrada de
    tu buzón (Thunderbird, el webmail) y queda en su carpeta, leído. «Devolver a la bandeja» lo deja
    como estaba. «Eliminar» lo lleva a la Papelera de tu correo, de donde se recupera.
  </>,
  <>Hazlo también cuando acierte: la cifra de acierto de Estadísticas sale de ahí.</>,
  <>
    El botón dice lo que va a pasar: si pone «reenviar a…», al pulsarlo el correo sale hacia esa
    persona. Si solo pone «Correcto», solo se anota tu respuesta.
  </>,
  <>
    Al abrir un correo queda <strong>leído</strong> para todos los que ven ese buzón, y también en
    tu programa de correo, como cuando lo abres allí. «Marcar como no leído» lo vuelve a poner en
    negrita, aquí y allí.
  </>,
  <>
    Los enlaces del cuerpo no funcionan a propósito, para que el remitente no sepa que lo has
    abierto. Para seguir uno, abre el original en tu programa de correo.
  </>,
  <>
    <strong>Responder</strong>, <strong>Responder a todos</strong> y <strong>Reenviar</strong> lo
    mandan desde el buzón al que llegó, con el hilo bien encadenado, y dejan una copia en su carpeta
    de Enviados, como si lo hubieras escrito en Thunderbird. Lo enviado sale debajo del correo.
  </>,
];

/** One email: the subject, the body, the attachments, what the triage decided, and "Reprocesar". */
export function EmailDetailPage() {
  const { emailId } = useParams<{ emailId: string }>();
  const email = useEmailDetail(emailId);

  // Without a parameter the query never runs, so `isPending` would stay true forever and
  // the screen would be a spinner with nothing behind it.
  if (!emailId) {
    return (
      <EmptyState
        title="Correo no encontrado"
        hint="La dirección no lleva ningún identificador de correo."
        action={
          <Link to="/correos" className="link">
            Volver a la bandeja
          </Link>
        }
      />
    );
  }

  if (email.isPending) {
    return <Spinner variant="block" label="Cargando correo" />;
  }

  if (email.isError) {
    return <ErrorMessage error={email.error}>No se pudo abrir el correo</ErrorMessage>;
  }

  return <EmailDetailView email={email.data} />;
}

/**
 * What an offer email has to say about its draft (contract v7).
 *
 * The classification and the reading are two calls at the back, and the second one fails
 * on its own: a 504 with a PDF leaves the email "Procesado", labelled as an offer, with
 * no quote behind it and nothing on the row saying so. Until v7 that was the same screen
 * as an offer with a draft, and the only thing anybody could do was nothing. `quote_id`
 * is what tells the two apart, and a re-read is the same `POST /reprocess`: the back
 * skips the classification it already has and retries only the reading.
 *
 * Since contract v10.7 `quote_id` is the email's **newest** quote and `quote_status` its
 * state: a cancelled one is a dead end unless it is redone, so the screen offers exactly
 * that («Rehacer presupuesto») instead of a link into the dead end.
 */
type OfferState = 'quoted' | 'cancelled' | 'unread' | 'none';

function offerStateOf(email: EmailDetail, category: string | undefined): OfferState {
  if (category !== 'oferta_proveedor_respuesta') return 'none';
  if (email.quote_id) return email.quote_status === 'cancelled' ? 'cancelled' : 'quoted';
  // Still pending or in error: the reading has not had its chance yet, or the row
  // already shows the error and its own Reprocesar.
  return email.status === 'processed' ? 'unread' : 'none';
}

function EmailDetailView({ email }: { email: EmailDetail }) {
  const reprocess = useReprocess(email.id);
  const rescue = useRescue(email.id);
  // The list this email was opened from, from the address (contract v10.25): «← Bandeja»
  // goes back to it and «Anterior» and «Siguiente» walk it. A pasted link has none and
  // walks «Por revisar».
  const [searchParams] = useSearchParams();
  const listParams = listFilters(searchParams);
  const neighbours = useNeighbours(email.id, listParams);
  const trash = useMoveEmail(email.id, 'trash');
  const untrash = useMoveEmail(email.id, 'untrash');
  const returnToInbox = useMoveEmail(email.id, 'returnToInbox');
  const moving = trash.isPending || untrash.isPending || returnToInbox.isPending;
  const moveError = trash.error ?? untrash.error ?? returnToInbox.error;
  // Unconditional: hooks cannot depend on the offer state. It only ever fires from the
  // «Rehacer» button, which only exists when `quote_id` is set.
  const redo = useDuplicateQuote(email.quote_id ?? '');
  const navigate = useNavigate();
  const location = useLocation();
  const rescuing = rescueInFlight(email) || rescue.isPending;
  const { mutate: markRead } = useMarkRead(email.id);
  const markUnread = useMarkUnread(email.id);
  // Opening it is reading it (contract v10.22), decided once per email, on the state it
  // had when it opened: after «Marcar como no leído» the cache says unread again and this
  // must not mark it straight back, also when it was already read on arrival. The GET of
  // the detail never marks, on purpose, for the same reason. Read is the mailbox's since
  // v10.43 (ADR 0037 of the back), so a superadministrator looking into a problem marks
  // nothing: what Victor sees, here and in Thunderbird, stays as it was.
  const platform = useIsPlatform();
  const seenId = useRef<string | null>(null);
  const unread = isUnread(email);
  useEffect(() => {
    if (seenId.current === email.id) return;
    seenId.current = email.id;
    if (unread && !platform) markRead();
  }, [email.id, unread, platform, markRead]);
  const readButton = email.is_read !== undefined && !platform;
  function leaveUnread(): void {
    markUnread.mutate(undefined, {
      // Back to where the person came from, the filtered list included; straight to the
      // inbox when this page was opened on its own (a pasted link).
      onSuccess: () => void (location.key === 'default' ? navigate('/correos') : navigate(-1)),
    });
  }
  const { mutation: download, blockedLink } = useDownloadAttachment(email.id);
  const allAttachments = email.attachments ?? [];
  // The images the body draws where the sender put them (`cid:`, contract v10.19) are
  // not listed again as downloads, like a mail client does with a signature logo.
  const inlineImages = useMemo(
    () => inlineImagesFor(email.id, email.attachments ?? [], apiUrl, window.location.href),
    [email.id, email.attachments],
  );
  const drawnInBody = referencedContentIds(email.body_html);
  const attachments = allAttachments.filter(
    (attachment) => !drawnInBody.has(attachmentContentId(attachment) ?? ''),
  );
  // Sorted once, and both halves derived from the same list. Two independent
  // assumptions about the same ordering is how they drift apart.
  const decisions = sortedDecisions(email);
  // The live one (contract v10.24): the newest a person answered, else the newest.
  const current = liveDecision(email);
  const older = decisions.filter((decision) => decision !== current);
  // Contract v10.47: a profile without the quotes (the shop, a salesperson) reads the
  // offer as mail and nothing more; the draft, its link and «Rehacer» are not theirs, and
  // the API would answer 403 to all three.
  const quotes = useCan('quotes');
  const offer = quotes
    ? offerStateOf(email, current ? decisionCategory(current) : undefined)
    : 'none';
  // Which button was pressed, remembered: the refetch after a successful re-read flips
  // the row to `pending` and `offer` to 'none', and the success line must not change its
  // story halfway through.
  const [requeued, setRequeued] = useState<'triage' | 'reread' | null>(null);
  function requeue(kind: 'triage' | 'reread'): void {
    setRequeued(kind);
    reprocess.mutate();
  }

  const sender = email.from_name
    ? `${email.from_name} <${email.from_address}>`
    : email.from_address;
  // Only a message that came through a mailbox, and is not sitting in the server's spam
  // folder (there, only «Rescatar»), can be moved in it.
  const movable = email.mailbox !== MANUAL_MAILBOX && !email.in_spam;
  // Replying and forwarding (contract v10.40): anything that came through a mailbox, not
  // a document uploaded by hand, which has nobody to answer.
  const answerable = email.mailbox !== MANUAL_MAILBOX;
  const [composing, setComposing] = useState<OutgoingKind | null>(null);
  // Contract v10.48: a supplier's Excel goes to Documentos from here, without downloading
  // it and uploading it again. Only for whoever manages the documents (the API says 403
  // to anybody else).
  const loadsTariffs = useCan('manage_documents');
  const [loadingTariff, setLoadingTariff] = useState<TariffAttachment | null>(null);

  // One primary action in the header: the draft when there is one, the rescue when the
  // mail sits in spam, the reprocess when it failed. When the triage panel has its own
  // Correcto, Reprocesar steps down to secondary so the screen keeps one red button.
  const headerAction =
    offer === 'cancelled' ? (
      <>
        <Button
          icon={<CopyIcon size={18} />}
          disabled={redo.isPending}
          onClick={() =>
            redo.mutate(undefined, {
              onSuccess: (draft) => void navigate(`/presupuestos/${draft.id}`),
            })
          }
        >
          {redo.isPending ? 'Rehaciendo…' : 'Rehacer presupuesto'}
        </Button>
        <Link to={`/presupuestos/${email.quote_id}`} className="link text-[13.5px]">
          Ver el anulado
        </Link>
      </>
    ) : offer === 'quoted' ? (
      <Link
        to={`/presupuestos/${email.quote_id}`}
        className="bg-accent hover:bg-accent-2 focus-visible:outline-accent inline-flex h-9 items-center rounded-md px-3.5 text-[14px] font-medium text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        Ver el presupuesto
      </Link>
    ) : email.in_spam && !email.rescued_at ? (
      <Button onClick={() => rescue.mutate()} disabled={rescuing}>
        {rescuing ? 'Rescatando…' : 'Rescatar a la bandeja'}
      </Button>
    ) : email.status === 'error' ? (
      <Button
        variant={current ? 'secondary' : 'primary'}
        icon={<RefreshIcon size={18} />}
        onClick={() => requeue('triage')}
        disabled={reprocess.isPending}
      >
        {reprocess.isPending ? 'Encolando…' : 'Reprocesar'}
      </Button>
    ) : null;

  return (
    <>
      <PageHeader
        back={
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link
              to={inboxHref(searchParams)}
              className="text-ink-3 hover:text-ink inline-flex items-center gap-1"
            >
              <ArrowLeftIcon size={15} />
              Bandeja
            </Link>
            <Walk
              previous={neighbours.data?.previous ?? null}
              next={neighbours.data?.next ?? null}
              position={neighbours.data?.position ?? null}
              total={neighbours.data?.total ?? null}
              params={searchParams}
            />
          </div>
        }
        title={<span className="line-clamp-2">{email.subject ?? '(sin asunto)'}</span>}
        help={HELP}
        badge={
          email.status !== 'processed' || email.in_spam || email.rescued_at || email.answered_at ? (
            <span className="flex flex-wrap gap-1.5">
              {email.status !== 'processed' ? (
                <StatusBadge status={email.status} retrying={isRetrying(email.auto_retry)} />
              ) : null}
              <SpamBadge email={email} />
              <AnsweredBadge email={email} />
            </span>
          ) : null
        }
        description={
          <>
            {sender}
            {email.to_address ? ` · a ${email.to_address}` : ''}
            {' · '}
            <time dateTime={email.received_at}>
              {formatDate(headerDateFormat, email.received_at)}
            </time>
          </>
        }
        actions={
          headerAction || readButton || movable ? (
            <>
              {headerAction}
              {movable && !email.trashed_at ? (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<TrashIcon size={15} />}
                  title="Se mueve a la Papelera de tu correo. Puedes recuperarlo."
                  onClick={() => trash.mutate()}
                  disabled={moving}
                >
                  {trash.isPending ? 'Eliminando…' : 'Eliminar'}
                </Button>
              ) : null}
              {/* Only when the back knows about read marks (v10.22), and never for a
                  superadministrator, who marks nothing (v10.43). */}
              {readButton ? (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<MailIcon size={15} />}
                  onClick={leaveUnread}
                  disabled={markUnread.isPending}
                >
                  {markUnread.isPending ? 'Marcando…' : 'Marcar como no leído'}
                </Button>
              ) : null}
            </>
          ) : null
        }
      />

      {markUnread.isError ? (
        <ErrorMessage error={markUnread.error}>No se pudo marcar como no leído</ErrorMessage>
      ) : null}
      {moveError ? (
        <ErrorMessage error={moveError}>No se pudo mover en tu correo</ErrorMessage>
      ) : null}

      {email.trashed_at ? (
        <Callout
          tone="neutral"
          compact
          role="status"
          title="En la Papelera de tu correo."
          action={
            <Button
              size="sm"
              variant="secondary"
              onClick={() => untrash.mutate()}
              disabled={moving}
            >
              {untrash.isPending ? 'Recuperando…' : 'Recuperar'}
            </Button>
          }
        >
          Desde el {formatDate(headerDateFormat, email.trashed_at)}. «Recuperar» lo devuelve a donde
          estaba.
        </Callout>
      ) : null}

      <OutOfInboxNotice email={email} />

      {attachments.length > 0 ? (
        <div className="-mt-2 flex flex-wrap items-center gap-2">
          {attachments.map((attachment) => (
            <span key={attachment.id} className="flex max-w-full flex-wrap items-center gap-1">
              <Button
                variant="secondary"
                size="sm"
                icon={<PaperclipIcon size={14} />}
                title="Descargar"
                onClick={() => download.mutate(attachment.id)}
                disabled={download.isPending && download.variables === attachment.id}
                className="max-w-full"
              >
                <span className="truncate">{attachment.filename}</span>
                <span className="text-ink-3 font-normal">{formatBytes(attachment.size_bytes)}</span>
              </Button>
              {loadsTariffs && isTariffFile(attachment.filename) ? (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<UploadIcon size={14} />}
                  aria-label={`Cargar como tarifa ${attachment.filename}`}
                  onClick={() =>
                    setLoadingTariff({
                      emailId: email.id,
                      attachmentId: attachment.id,
                      filename: attachment.filename,
                      fromAddress: email.from_address,
                    })
                  }
                >
                  Cargar como tarifa
                </Button>
              ) : null}
            </span>
          ))}
          {download.isError ? (
            <ErrorMessage error={download.error}>No se pudo descargar el adjunto</ErrorMessage>
          ) : null}
          {blockedLink ? (
            <p role="status" className="text-ink-2 text-[13.5px]">
              El navegador bloqueó la ventana emergente.{' '}
              <a className="link" href={blockedLink.url} target="_blank" rel="noopener noreferrer">
                Abrir {blockedLink.filename}
              </a>
            </p>
          ) : null}
        </div>
      ) : null}

      {email.in_spam ? (
        <Callout tone="warn" compact title="Este correo estaba en la carpeta de spam" role="status">
          El sistema lo ha leído pero no actuará sobre él. «Rescatar a la bandeja» deja una copia en
          la bandeja de entrada; el original se queda en spam.
          {email.rescued_at ? (
            <span className="block">
              Rescatado. La copia aparecerá en la bandeja en unos segundos.
            </span>
          ) : null}
          {email.rescue_error ? (
            <div role="alert" className="text-danger">
              No se pudo rescatar: {rescueFailureText(email.rescue_error)}
              <TechnicalDetails text={email.rescue_error} />
            </div>
          ) : null}
          {rescue.isError ? (
            <span role="alert" className="text-danger block">
              {errorMessage(rescue.error)}
            </span>
          ) : null}
        </Callout>
      ) : null}

      {email.status === 'error' ? (
        isRetrying(email.auto_retry) ? (
          <Callout tone="info" compact role="status" title="Se está reintentando solo.">
            {failureText(email.error_code)} {retryLine(email.auto_retry)}
            <FailureCode code={email.error_code} />
          </Callout>
        ) : (
          <Callout tone="danger" compact title="No se pudo procesar este correo.">
            {failureText(email.error_code)}{' '}
            {email.auto_retry?.exhausted ? `${retryLine(email.auto_retry)} ` : null}
            «Reprocesar» vuelve a intentarlo sin mandar nada a nadie. Si vuelve a fallar, avisa a
            quien administra la aplicación con este código.
            <FailureCode code={email.error_code} />
          </Callout>
        )
      ) : null}

      {reprocess.isError ? (
        <ErrorMessage error={reprocess.error}>No se pudo volver a procesar</ErrorMessage>
      ) : null}
      {redo.isError ? <ErrorMessage error={redo.error}>No se pudo rehacer</ErrorMessage> : null}
      {reprocess.isSuccess ? (
        <Callout tone="ok" compact role="status">
          {requeued === 'reread'
            ? 'Encolado de nuevo. Si esta vez se lee, el borrador aparecerá en Presupuestos en unos segundos: recarga esta página para verlo.'
            : 'Encolado de nuevo. Se volverá a clasificar en unos segundos.'}
        </Callout>
      ) : null}

      {offer === 'unread' && isRetrying(email.auto_retry) ? (
        <Callout
          tone="info"
          compact
          role="status"
          title="La oferta se clasificó y su lectura se está reintentando sola."
        >
          {failureText(email.reading_error_code)} {retryLine(email.auto_retry)} El borrador
          aparecerá en Presupuestos cuando se lea.
          <FailureCode code={email.reading_error_code} />
        </Callout>
      ) : offer === 'unread' ? (
        <Callout
          tone="warn"
          compact
          title="La oferta se clasificó pero no se pudo leer: no hay borrador."
          action={
            <Button
              size="sm"
              variant="secondary"
              onClick={() => requeue('reread')}
              disabled={reprocess.isPending}
            >
              {reprocess.isPending ? 'Encolando…' : 'Volver a leer la oferta'}
            </Button>
          }
        >
          {email.reading_error_code ? `${failureText(email.reading_error_code)} ` : null}
          {email.auto_retry?.exhausted ? `${retryLine(email.auto_retry)} ` : null}
          Pulsa el botón y espera medio minuto.
          <FailureCode code={email.reading_error_code} />
        </Callout>
      ) : null}

      {/* Phone: the panel first (the action), the body after. Desktop: body left, panel
          right and sticky. Same DOM, the order is CSS. */}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] lg:items-start">
        <div className="order-2 flex flex-col gap-4 lg:order-1">
          {answerable ? (
            <div className="flex flex-wrap items-center gap-2" aria-label="Responder o reenviar">
              <Button
                variant="secondary"
                size="sm"
                icon={<ReplyIcon size={15} />}
                onClick={() => setComposing('reply')}
              >
                Responder
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<ReplyAllIcon size={15} />}
                onClick={() => setComposing('reply_all')}
              >
                Responder a todos
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<ForwardIcon size={15} />}
                onClick={() => setComposing('forward')}
              >
                Reenviar
              </Button>
            </div>
          ) : null}
          <Conversation emailId={email.id} size={email.conversation_size ?? 1} />
          <Card padded={false}>
            <EmailBody
              text={email.body_text ?? null}
              html={email.body_html ?? null}
              inlineImages={inlineImages}
            />
          </Card>

          <SentFromHere emailId={email.id} enabled={answerable} />

          <details className="text-[13px]">
            <summary className="text-ink-4 hover:text-ink cursor-pointer select-none">
              Detalles técnicos
            </summary>
            <dl className="text-ink-3 mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-[8rem_minmax(0,1fr)]">
              <dt>Identificador</dt>
              <dd className="font-mono text-[12px] break-all">{email.message_id}</dd>
              <dt>Buzón</dt>
              <dd className="break-all">
                {mailboxLabel(email.mailbox)}
                {email.folder ? ` · ${email.folder}` : ''}
              </dd>
              <dt>Recibido</dt>
              <dd className="font-mono text-[12px]">{email.received_at}</dd>
              {email.error_code ? (
                <>
                  <dt>Código de error</dt>
                  <dd className="text-danger font-mono text-[12px]">{email.error_code}</dd>
                </>
              ) : null}
              {/* The internal text: the API sends it to superadministrators only (v10.31). */}
              {email.error ? (
                <>
                  <dt>Error</dt>
                  <dd className="text-danger font-mono text-[12px] break-all">{email.error}</dd>
                </>
              ) : null}
            </dl>
          </details>
        </div>

        <div className="order-1 flex flex-col gap-4 lg:sticky lg:top-6 lg:order-2">
          <Card title="Qué es y a quién va">
            {current ? (
              <TriagePanel
                emailId={email.id}
                decision={current}
                plan={email.triage_plan}
                movedTo={email.location_folder ?? null}
                noteLearning={email.note_learning ?? null}
              />
            ) : (
              <p className="text-ink-3 text-[13.5px]">
                Sin decisión todavía: el sistema aún no ha clasificado este correo.
              </p>
            )}

            {current && movable && !email.trashed_at ? (
              <InMailbox
                email={email}
                answered={Boolean(email.reviewed)}
                onReturn={() => returnToInbox.mutate()}
                busy={moving}
              />
            ) : null}

            {email.reviewed || email.trashed_at ? (
              <NextToReview current={email.id} mailbox={searchParams.get(PARAM.mailbox) ?? ''} />
            ) : null}

            {older.length > 0 ? (
              <details className="text-[13px]">
                <summary className="text-ink-4 hover:text-ink cursor-pointer select-none">
                  Historial ({older.length})
                </summary>
                <ul className="mt-2 flex flex-col gap-1">
                  {older.map((decision) => (
                    <li
                      key={decision.id}
                      className="border-line bg-paper text-ink-2 rounded-md border p-3"
                    >
                      {CATEGORY_LABELS[decision.category]}, propuesto:{' '}
                      {DESTINATION_LABELS[decision.proposed_destination]}
                      {decision.rationale ? <p className="mt-1">{decision.rationale}</p> : null}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </Card>
        </div>
      </div>

      {composing ? (
        <ComposeDialog
          kind={composing}
          sourceEmailId={email.id}
          onClose={() => setComposing(null)}
        />
      ) : null}

      {loadingTariff ? (
        <LoadAsTariffDialog attachment={loadingTariff} onClose={() => setLoadingTariff(null)} />
      ) : null}

      <AiNotice className="mt-auto" />
    </>
  );
}

/**
 * «‹ Anterior · 3 de 12 · Siguiente ›» within the list the email was opened from
 * (contract v10.25). Nothing while the neighbours load, and the position only while the
 * email is still in the list (just answered, it has left «Por revisar»).
 */
function Walk({
  previous,
  next,
  position,
  total,
  params,
}: {
  previous: string | null;
  next: string | null;
  position: number | null;
  total: number | null;
  params: URLSearchParams;
}) {
  if (total === null) return null;
  return (
    <nav aria-label="Otros correos de la lista" className="flex items-center gap-1 text-[13px]">
      {previous ? (
        <Link
          to={emailHref(previous, params)}
          className="text-ink-3 hover:text-ink inline-flex h-8 items-center gap-1 rounded-md px-2"
        >
          <ArrowLeftIcon size={14} />
          Anterior
        </Link>
      ) : null}
      {position !== null ? (
        <span className="text-ink-4 px-1 tabular-nums">
          {position} de {total}
        </span>
      ) : null}
      {next ? (
        <Link
          to={emailHref(next, params)}
          className="text-ink-3 hover:text-ink inline-flex h-8 items-center gap-1 rounded-md px-2"
        >
          Siguiente
          <ArrowRightIcon size={14} />
        </Link>
      ) : null}
    </nav>
  );
}

const movedFormat = new Intl.DateTimeFormat('es-ES', { timeStyle: 'short' });

/**
 * Where the email is in the person's own mailbox, and the way back (contract v10.25).
 * After «Correcto» the original left the inbox for its folder; «Devolver a la bandeja»
 * puts it back unread and the email back in «Por revisar». A mailbox that only copies
 * never moved it, and then the button only reopens it here.
 */
function InMailbox({
  email,
  answered,
  onReturn,
  busy,
}: {
  email: EmailDetail;
  answered: boolean;
  onReturn: () => void;
  busy: boolean;
}) {
  const folder = email.location_folder ? folderShortName(email.location_folder) : null;
  if (!folder && !answered) return null;
  // Out of the inbox from the mail client (contract v10.34): `OutOfInboxNotice` says so,
  // and «Volver a «Por revisar»» would not bring it back there.
  if (!folder && email.location_unknown_at) return null;
  // Between «Correcto» and the worker's answer the mail is on its way: saying «sigue en
  // la bandeja de entrada» then would be true for a second and read as a failure.
  if (actionInFlight(email)) {
    return (
      <p role="status" className="border-line-2 text-ink-3 border-t pt-3 text-[13.5px]">
        Ordenándolo en tu correo…
      </p>
    );
  }
  return (
    <div className="border-line-2 flex flex-col gap-2 border-t pt-3 text-[13.5px]">
      <p className="text-ink-2 flex items-start gap-2">
        <InboxIcon size={16} className="text-ink-4 mt-0.5 shrink-0" />
        <span>
          {folder ? (
            <>
              En tu correo está en la carpeta <strong className="text-ink">«{folder}»</strong>
              {email.moved_at ? (
                <span className="text-ink-4">
                  {' · '}
                  movido a las {formatDate(movedFormat, email.moved_at)}
                </span>
              ) : null}
              .
            </>
          ) : (
            'En tu correo sigue en la bandeja de entrada.'
          )}
        </span>
      </p>
      <div>
        <Button variant="ghost" size="sm" onClick={onReturn} disabled={busy}>
          {busy ? 'Devolviendo…' : folder ? 'Devolver a la bandeja' : 'Volver a «Por revisar»'}
        </Button>
      </div>
    </div>
  );
}

/**
 * «Siguiente por revisar →»: what is left to do, from the newest (contract v10.25). Asked
 * at the click and not before: the list changes under the person as they answer, and a
 * link worked out when the page opened would point at one they already did.
 */
function NextToReview({ current, mailbox }: { current: string; mailbox: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [looking, setLooking] = useState(false);
  const [none, setNone] = useState(false);
  const params: EmailListParams = { view: 'por_revisar', ...(mailbox ? { mailbox } : {}) };
  const place = new URLSearchParams(mailbox ? { [PARAM.mailbox]: mailbox } : {});
  async function go(): Promise<void> {
    setLooking(true);
    try {
      const found = await queryClient.fetchQuery({
        queryKey: emailKeys.neighbours(current, params),
        queryFn: () => emailsApi.neighbours(current, params),
        staleTime: 0,
      });
      const target = found.next ?? found.first;
      if (target) void navigate(emailHref(target, place));
      else setNone(true);
    } finally {
      setLooking(false);
    }
  }
  if (none) {
    return (
      <p role="status" className="bg-ok-soft text-ok rounded-md px-3 py-2 text-[13.5px]">
        No queda nada por revisar.{' '}
        <Link to={inboxHref(place)} className="link">
          Volver a la bandeja
        </Link>
      </p>
    );
  }
  return (
    <Button onClick={() => void go()} disabled={looking} icon={<ArrowRightIcon size={18} />}>
      {looking ? 'Buscando…' : 'Siguiente por revisar'}
    </Button>
  );
}
